// The efmodel format: a logical, design-oriented description of an EF Core model, shared by the app,
// the command-line tool and AI agents (`*.efmodel.json`). Zod defines it; the JSON Schema published for
// editors and agents is generated from these definitions.
import { z } from 'zod';
import type { JsonValue } from '../ef/values';

export const FORMAT = 'efmodel';
export const FORMAT_VERSION = '1.0';
export const SCHEMA_URL = 'https://modelviewer-7rw.pages.dev/schema/efmodel-1.json';

export const JsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([z.null(), z.boolean(), z.number(), z.string(), z.array(JsonValueSchema), z.record(z.string(), JsonValueSchema)]),
);

const Name = z.string().min(1);
const NameList = z.array(Name).min(1);
const Extensions = z.record(z.string(), JsonValueSchema).describe('Free-form data for other tools. Ignored by modelviewer.');
const Annotations = z.record(z.string(), JsonValueSchema);
const Calls = z.array(z.string()).describe('Provider-specific builder calls kept verbatim, e.g. "UseIdentityColumn()".');

export const PropertyPhysicalSchema = z
  .object({
    column: z.string().optional().describe('Column name when it differs from the property name.'),
    columnType: z.string().optional(),
    defaultSql: z.string().optional(),
    computedSql: z.string().optional(),
    stored: z.boolean().optional(),
    unicode: z.boolean().optional(),
    fixedLength: z.boolean().optional(),
    collation: z.string().optional(),
    jsonName: z.string().optional(),
    columnOrder: z.number().int().optional(),
    annotations: Annotations.optional(),
    calls: Calls.optional(),
  })
  .strict();

export const PropertySchema = z
  .object({
    type: Name.describe('C# type without namespace or trailing ?, e.g. int, string, decimal, DateTime, Guid, byte[].'),
    required: z.boolean().describe('False when the value may be null (nullable reference type or Nullable<T>).'),
    maxLength: z.number().int().positive().optional(),
    precision: z.number().int().positive().optional(),
    scale: z.number().int().min(0).optional(),
    generated: z.enum(['onAdd', 'onUpdate', 'onAddOrUpdate', 'onUpdateSometimes']).optional().describe('When the database generates the value.'),
    concurrencyToken: z.boolean().optional(),
    default: JsonValueSchema.optional().describe('Default value (a constant, not SQL).'),
    collection: z.boolean().optional().describe('A primitive collection such as List<string>, stored as one column.'),
    comment: z.string().optional(),
    physical: PropertyPhysicalSchema.optional(),
    extensions: Extensions.optional(),
  })
  .strict();

export const IndexSchema = z
  .object({
    properties: NameList,
    unique: z.boolean().optional(),
    name: z.string().optional().describe('Index name in the model (EF Core 5+).'),
    filter: z.string().optional(),
    descending: z.array(z.boolean()).optional(),
    physical: z.object({ name: z.string().optional(), annotations: Annotations.optional(), calls: Calls.optional() }).strict().optional(),
  })
  .strict();

export const CheckSchema = z.object({ name: Name, sql: z.string() }).strict();

export const MappingSchema = z
  .object({
    table: z.string().nullable().optional().describe('Table name; null means not mapped to a table.'),
    schema: z.string().optional(),
    view: z.string().optional(),
    viewSchema: z.string().optional(),
    function: z.string().optional(),
    sqlQuery: z.string().optional(),
    strategy: z.enum(['tph', 'tpt', 'tpc']).optional().describe('Inheritance mapping, on the root of a hierarchy.'),
    discriminator: z.string().optional().describe('Discriminator property name, on the root of a hierarchy.'),
    discriminatorValue: JsonValueSchema.optional(),
    discriminatorComplete: z.boolean().optional(),
    json: z.string().optional().describe('Stored as JSON in this column (owned or complex types).'),
    jsonColumnType: z.string().optional(),
    excludedFromMigrations: z.boolean().optional(),
  })
  .strict();

const PhysicalSchema = z.object({ annotations: Annotations.optional(), calls: Calls.optional() }).strict();

export interface ComplexSpec {
  type: string;
  clrName?: string;
  collection?: boolean;
  required: boolean;
  properties: Record<string, PropertySpec>;
  complex?: Record<string, ComplexSpec>;
  mapping?: MappingSpec;
  comment?: string;
  physical?: PhysicalSpec;
  extensions?: Record<string, JsonValue>;
}

export const ComplexSchema: z.ZodType<ComplexSpec> = z.lazy(() =>
  z
    .object({
      type: Name.describe('Name of the complex type, e.g. Money.'),
      clrName: z.string().optional(),
      collection: z.boolean().optional(),
      required: z.boolean(),
      properties: z.record(Name, PropertySchema),
      complex: z.record(Name, ComplexSchema).optional(),
      mapping: MappingSchema.optional(),
      comment: z.string().optional(),
      physical: PhysicalSchema.optional(),
      extensions: Extensions.optional(),
    })
    .strict(),
);

export interface OwnedSpec {
  type: string;
  clrName?: string;
  collection?: boolean;
  required?: boolean;
  ownerKey: string[];
  key?: string[];
  properties: Record<string, PropertySpec>;
  owned?: Record<string, OwnedSpec>;
  complex?: Record<string, ComplexSpec>;
  indexes?: IndexSpec[];
  checks?: CheckSpec[];
  mapping?: MappingSpec;
  comment?: string;
  seed?: Record<string, JsonValue>[];
  physical?: PhysicalSpec;
  extensions?: Record<string, JsonValue>;
}

export const OwnedSchema: z.ZodType<OwnedSpec> = z.lazy(() =>
  z
    .object({
      type: Name.describe('Name of the owned type, e.g. Address.'),
      clrName: z.string().optional(),
      collection: z.boolean().optional().describe('OwnsMany instead of OwnsOne.'),
      required: z.boolean().optional().describe('The owner must always have this owned value.'),
      ownerKey: z.array(Name).describe('Properties of the owned type that point back to the owner.'),
      key: NameList.optional().describe('Primary key of the owned type when it differs from ownerKey.'),
      properties: z.record(Name, PropertySchema),
      owned: z.record(Name, OwnedSchema).optional(),
      complex: z.record(Name, ComplexSchema).optional(),
      indexes: z.array(IndexSchema).optional(),
      checks: z.array(CheckSchema).optional(),
      mapping: MappingSchema.optional(),
      comment: z.string().optional(),
      seed: z.array(z.record(z.string(), JsonValueSchema)).optional(),
      physical: PhysicalSchema.optional(),
      extensions: Extensions.optional(),
    })
    .strict(),
);

export const EntitySchema = z
  .object({
    clrName: z.string().optional().describe('Full C# class name, e.g. MyApp.Domain.Blog.'),
    base: Name.optional().describe('Base entity (inheritance).'),
    keyless: z.boolean().optional(),
    key: NameList.optional().describe('Primary key properties. Omitted on derived and keyless entities.'),
    alternateKeys: z.array(NameList).optional(),
    mapping: MappingSchema.optional(),
    comment: z.string().optional(),
    properties: z.record(Name, PropertySchema),
    owned: z.record(Name, OwnedSchema).optional().describe('Owned types, keyed by navigation name.'),
    complex: z.record(Name, ComplexSchema).optional().describe('Complex types, keyed by property name.'),
    indexes: z.array(IndexSchema).optional(),
    checks: z.array(CheckSchema).optional(),
    seed: z.array(z.record(z.string(), JsonValueSchema)).optional(),
    physical: PhysicalSchema.optional(),
    extensions: Extensions.optional(),
  })
  .strict();

export const DeleteBehaviorSchema = z.enum(['cascade', 'clientCascade', 'restrict', 'setNull', 'clientSetNull', 'noAction', 'clientNoAction']);

export const RelationshipSchema = z
  .object({
    dependent: Name.describe('Entity holding the foreign key; owned types use Owner.Navigation.'),
    principal: Name,
    foreignKey: NameList,
    principalKey: NameList.optional().describe("Omitted when it is the principal's primary key."),
    cardinality: z.enum(['one-to-many', 'one-to-one']),
    required: z.boolean().describe('Every dependent must have a principal.'),
    requiredDependent: z.boolean().optional().describe('One-to-one only: every principal must have a dependent.'),
    onDelete: DeleteBehaviorSchema,
    dependentNavigation: z.string().optional(),
    principalNavigation: z.string().optional(),
    physical: z.object({ constraintName: z.string().optional(), annotations: Annotations.optional() }).strict().optional(),
    extensions: Extensions.optional(),
  })
  .strict();

export const SequenceSchema = z
  .object({
    name: Name,
    schema: z.string().optional(),
    type: Name,
    start: z.number().optional(),
    increment: z.number().optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    cyclic: z.boolean().optional(),
  })
  .strict();

export const ModelSchema = z
  .object({
    defaultSchema: z.string().optional(),
    entities: z.record(Name, EntitySchema).describe('Entities keyed by short name.'),
    relationships: z.array(RelationshipSchema),
    sequences: z.array(SequenceSchema).optional(),
    physical: PhysicalSchema.optional(),
    extensions: Extensions.optional(),
  })
  .strict();

export const RenameSchema = z
  .object({
    entity: Name.optional().describe('For a property rename: the entity (its new name). Omit for an entity rename.'),
    from: Name,
    to: Name,
  })
  .strict();

export const DocumentSchema = z
  .object({
    $schema: z.string().optional(),
    format: z.literal(FORMAT),
    formatVersion: z.string().regex(/^1\.\d+$/, 'formatVersion must be 1.x'),
    title: Name,
    description: z.string().optional(),
    useCase: z.string().optional().describe('The situation this design is for.'),
    rationale: z.string().optional().describe('Why this design; trade-offs considered.'),
    basedOn: z
      .object({
        context: z.string().optional(),
        migration: z.string().optional(),
        sketch: z.string().optional(),
        modelHash: z.string().optional(),
      })
      .strict()
      .optional()
      .describe('The model this one was derived from.'),
    source: z
      .object({
        kind: z.enum(['snapshot', 'migration']),
        context: z.string(),
        migration: z.string().optional(),
        efVersion: z.string().optional(),
        file: z.string().optional(),
      })
      .strict()
      .optional()
      .describe('Where an extracted model came from.'),
    renames: z.array(RenameSchema).optional().describe('Renames relative to basedOn, so comparisons pair old and new names.'),
    model: ModelSchema,
    extensions: Extensions.optional(),
  })
  .strict();

export type PropertySpec = z.infer<typeof PropertySchema>;
export type PropertyPhysicalSpec = z.infer<typeof PropertyPhysicalSchema>;
export type IndexSpec = z.infer<typeof IndexSchema>;
export type CheckSpec = z.infer<typeof CheckSchema>;
export type MappingSpec = z.infer<typeof MappingSchema>;
export type PhysicalSpec = z.infer<typeof PhysicalSchema>;
export type EntitySpec = z.infer<typeof EntitySchema>;
export type RelationshipSpec = z.infer<typeof RelationshipSchema>;
export type DeleteBehavior = z.infer<typeof DeleteBehaviorSchema>;
export type SequenceSpec = z.infer<typeof SequenceSchema>;
export type ModelSpec = z.infer<typeof ModelSchema>;
export type RenameSpec = z.infer<typeof RenameSchema>;
export type EfModelDocument = z.infer<typeof DocumentSchema>;

/** JSON Schema (draft 2020-12) for `*.efmodel.json` files. */
export function efmodelJsonSchema(): Record<string, unknown> {
  const schema = z.toJSONSchema(DocumentSchema, { target: 'draft-2020-12', unrepresentable: 'any' }) as Record<string, unknown>;
  return {
    $schema: 'https://json-schema.org/draft/2020-12/schema',
    $id: SCHEMA_URL,
    title: 'EF Core model (efmodel 1.x)',
    description: 'A logical EF Core model or design sketch, as used by modelviewer and AI agents.',
    ...Object.fromEntries(Object.entries(schema).filter(([k]) => k !== '$schema')),
  };
}
